import useConfig from 'UI/customHooks/useConfig';

interface AvailableLanguage {
  languageCode: string;
  languageName: string;
  isDefault?: boolean;
  isActive?: boolean;
}

interface UseAvailableLanguagesOptions {
  onlyLightcastSupported?: boolean;
  includeInactive?: boolean;
}

export const LightcastOntologyLanguages = [
  "cs",
  "da",
  "de",
  "en",
  "en-CA",
  "en-US",
  "es",
  "es-AR",
  "es-MX",
  "es-PR",
  "fr",
  "fr-CA",
  "hr",
  "hu",
  "it",
  "nl",
  "pl",
  "pt",
  "pt-BR",
  "ro",
  "sv"
] as const;

const filterLightcastLanguages = (onlyLightcastSupported: boolean, languages: AvailableLanguage[]): AvailableLanguage[] => {
  if (!onlyLightcastSupported) {
    return languages;
  }
  return languages.filter(lang => LightcastOntologyLanguages.includes(lang.languageCode as typeof LightcastOntologyLanguages[number]));
};

const useAvailableLanguages = ({ onlyLightcastSupported = false, includeInactive = false }: UseAvailableLanguagesOptions = {}): AvailableLanguage[] => {
  const config = useConfig();
  const languages = config?.cms?.features?.localization?.languages as Record<string, AvailableLanguage>;

  const availableLanguages =
    languages && Object.keys(languages).length > 0
      ? Object.keys(languages)
          .map(langKey => languages[langKey])
          .filter((item) => {
            if (!item) {
              return false;
            }

            if (includeInactive) {
              return true;
            }

            return item.isActive;
          })
      : [];

  const deprecatedAvailableLanguages =
    (config?.cms?.features?.localization?.availableLanguages as AvailableLanguage[]) ?? [];

  const finalLanguages = filterLightcastLanguages(onlyLightcastSupported, availableLanguages.length > 0 ? availableLanguages : deprecatedAvailableLanguages);

  // Ensure at least one language has isDefault flag for system reliability
  if (finalLanguages.length > 0 && !finalLanguages.some(lang => lang.isDefault)) {
    finalLanguages[0].isDefault = true;
  }

  if (finalLanguages.length === 0) {
    throw new Error('useAvailableLanguages: no available languages configured');
  }

  return finalLanguages;
};

export default useAvailableLanguages;
